import {
  backButton,
  bindThemeParamsCssVars,
  bindViewportCssVars,
  init,
  isTMA,
  miniAppReady,
  mountThemeParamsSync,
  mountViewport,
  retrieveRawInitData,
} from '@telegram-apps/sdk-react';

export type TelegramUnavailableReason = 'outside-telegram' | 'startup-failed' | 'no-init-data';

export type TelegramRuntime =
  | { status: 'ready'; rawInitData: string }
  | { status: 'unavailable'; reason: TelegramUnavailableReason };

let runtime: TelegramRuntime | undefined;

const isTelegramEnvironment = (): boolean => {
  try {
    return isTMA();
  } catch {
    return false;
  }
};

const bindThemeCssVars = (): void => {
  if (mountThemeParamsSync.isAvailable()) {
    mountThemeParamsSync();
  }

  if (bindThemeParamsCssVars.isAvailable()) {
    bindThemeParamsCssVars();
  }
};

const bindSafeAreaCssVars = (): void => {
  if (!mountViewport.isAvailable()) {
    return;
  }

  void mountViewport()
    .then(() => {
      if (bindViewportCssVars.isAvailable()) {
        bindViewportCssVars();
      }
    })
    .catch(() => undefined);
};

const prepareBackButton = (): void => {
  if (backButton.mount.isAvailable()) {
    backButton.mount();
  }

  if (backButton.hide.isAvailable()) {
    backButton.hide();
  }
};

const start = (): TelegramRuntime => {
  if (!isTelegramEnvironment()) {
    return { status: 'unavailable', reason: 'outside-telegram' };
  }

  try {
    init();
    bindThemeCssVars();
    bindSafeAreaCssVars();
    prepareBackButton();

    if (miniAppReady.isAvailable()) {
      miniAppReady();
    }
  } catch (error) {
    // Telegram is there but the SDK refused to start. Without this line the screen would claim
    // "open me in Telegram" to someone who already is in Telegram, and nothing would say why.
    console.error('Telegram SDK failed to start', error);

    return { status: 'unavailable', reason: 'startup-failed' };
  }

  const rawInitData = retrieveRawInitData();

  if (rawInitData === undefined || rawInitData.length === 0) {
    return { status: 'unavailable', reason: 'no-init-data' };
  }

  return { status: 'ready', rawInitData };
};

/**
 * Boots the Telegram SDK once and reports whether the app can run. Safe to call in a plain
 * browser: outside Telegram it returns `unavailable` instead of throwing, so the shell can show
 * an explanation rather than a blank page.
 */
export const startTelegram = (): TelegramRuntime => {
  runtime ??= start();

  return runtime;
};

export const getRawInitData = (): string | null => {
  const current = startTelegram();

  return current.status === 'ready' ? current.rawInitData : null;
};
